import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3, Eye, EyeOff, Languages, Lock, LogIn, Plus, ShieldCheck, Stethoscope, User } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { Alert, Button } from '@/components/ui';
import { AppDownloadCard, LoginStats, usePublicBranding, usePublicStats } from '@/components/LoginExtras';
import { HospitalLogo } from '@/components/HospitalLogo';
import { LanguageSwitcher, ThemeSwitcher } from '@/layout/TopbarWidgets';
import { loadOverrides } from '@/i18n';
import { errorMessage } from '@/utils/errors';
import { hospitalName } from '@/utils/format';

// TEMPORARY (testing): the sign-in form starts filled with the dev seed admin, and the demo account buttons
// fill both fields. Local development only, or a build with VITE_PREFILL_LOGIN=true. Remove before go-live.
const PREFILL = import.meta.env.DEV || import.meta.env.VITE_PREFILL_LOGIN === 'true';
const DEMO: Record<string, string> = {
  admin: 'Admin@12345', doctor: 'Doctor@12345', eyedoctor: 'Eye@12345', optometrist: 'Optom@12345', nurse: 'Nurse@12345',
  reception: 'Reception@12345', pharmacist: 'Pharma@12345', labtech: 'Lab@12345', cashier: 'Cashier@12345',
  storekeeper: 'Store@12345', approver: 'Approve@12345', imaging: 'Imaging@12345', accountant: 'Account@12345',
  reporter: 'Report@12345', reportapprover: 'Review@12345',
}; // backend/config/seed.dev.json

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState(PREFILL ? 'admin' : '');
  const [password, setPassword] = useState(PREFILL ? DEMO.admin : '');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stats = usePublicStats();
  // A tenant's own sign-in link (/login?h=CODE) shows its hospital and signs in to it.
  const hospitalCode = new URLSearchParams(location.search).get('h');
  const branding = usePublicBranding(hospitalCode);
  const linkedHospital = hospitalCode && branding.data?.hospitalId ? branding.data : null;

  // Global (organization-wide) custom wording applies before sign-in too.
  useEffect(() => {
    void loadOverrides(i18n.language, false);
  }, [i18n.language]);

  if (status === 'authenticated') return <Navigate to={(location.state as { from?: string })?.from ?? '/'} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password, linkedHospital?.hospitalId);
      navigate((location.state as { from?: string })?.from ?? '/', {
        replace: true,
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page login-anim">
      <section className="login-hero">
        <LoginBackdrop />
        <div className="row la-item" style={{ gap: 12, position: 'relative', zIndex: 1, ['--i' as string]: 0 }}>
          {branding.data?.hasPhoto ? (
            <HospitalLogo hospitalId={branding.data.hospitalId} hasPhoto version={branding.data.photoUpdatedAt} alt={branding.data.name} className="login-logo" size={44} />
          ) : (
            <div className="brand-logo login-logo" style={{ background: '#fff', color: 'var(--c-primary)' }}>
              <Plus size={20} strokeWidth={3} />
            </div>
          )}
          <span className="stack" style={{ gap: 2 }}>
            <strong style={{ fontSize: '1.2rem' }}>{branding.data?.name ? hospitalName(branding.data, i18n.language) : t('app.fullName')}</strong>
            {branding.data?.name && <span className="small" style={{ opacity: 0.85 }}>{t('app.fullName')}</span>}
          </span>
        </div>
        <div className="hero-extra" style={{ maxWidth: 480, position: 'relative', zIndex: 1 }}>
          <h1 className="la-item" style={{ fontSize: '2.4rem', lineHeight: 1.2, ['--i' as string]: 1 }}>
            {t('app.tagline')}
          </h1>
          {stats.data ? (
            <div className="la-item" style={{ ['--i' as string]: 2 }}>
              <LoginStats />
            </div>
          ) : (
            <div className="features">
              {[
                {
                  icon: <Stethoscope size={20} />,
                  text: t('auth.featureClinical'),
                },
                {
                  icon: <BarChart3 size={20} />,
                  text: t('auth.featureAnalytics'),
                },
                {
                  icon: <Languages size={20} />,
                  text: t('auth.featureLanguages'),
                },
                {
                  icon: <ShieldCheck size={20} />,
                  text: t('auth.featureSecurity'),
                },
              ].map((f, i) => (
                <div key={i} className="feature la-item" style={{ ['--i' as string]: i + 2 }}>
                  <span className="ic">{f.icon}</span>
                  <span style={{ paddingTop: 8 }}>{f.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="hero-extra small la-item" style={{ opacity: 0.8, position: 'relative', zIndex: 1, ['--i' as string]: 6 }}>
          ລາວ · ไทย · English · 中文 · Tiếng Việt
        </div>
      </section>
      <section className="login-form-wrap">
        <div className="stack" style={{ width: '100%', maxWidth: 420 }}>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
          <div className="login-card stack la-card">
            <div>
              <h1>{t('auth.signInTitle')}</h1>
              <p className="muted" style={{ marginTop: 6 }}>
                {t('auth.signInSubtitle')}
              </p>
            </div>
            {status === 'anonymous' && (location.state as { expired?: boolean })?.expired && (
              <Alert tone="warning">{t('auth.sessionExpired')}</Alert>
            )}
            {error && <Alert tone="danger">{error}</Alert>}
            <form className="stack la-form" onSubmit={submit}>
              <div className="field">
                <label htmlFor="username">{t('auth.username')}</label>
                <div className="input-icon">
                  <span className="lead">
                    <User size={18} />
                  </span>
                  <input
                    id="username"
                    className="input"
                    autoComplete="username"
                    autoFocus
                    required
                    placeholder={t('auth.usernamePlaceholder')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="password">{t('auth.password')}</label>
                <div className="input-icon">
                  <span className="lead">
                    <Lock size={18} />
                  </span>
                  <input
                    id="password"
                    className="input"
                    type={show ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ paddingRight: 46 }}
                  />
                  <span className="trail">
                    <Button
                      iconOnly
                      size="sm"
                      variant="ghost"
                      aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
                      onClick={() => setShow((s) => !s)}
                      icon={show ? <EyeOff size={18} /> : <Eye size={18} />}
                    />
                  </span>
                </div>
              </div>
              <Button type="submit" variant="primary" loading={busy} icon={<LogIn size={18} />} style={{ width: '100%' }}>
                {busy ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
            </form>
            {PREFILL && (
              <details className="small muted" open>
                <summary>{t('auth.demoAccounts')}</summary>
                <div className="chip-list" style={{ marginTop: 8 }}>
                  {Object.keys(DEMO).map((u) => (
                    <button
                      key={u}
                      type="button"
                      className={`chip ${u === username ? 'active' : ''}`}
                      onClick={() => {
                        setUsername(u);
                        setPassword(DEMO[u]);
                      }}
                    >
                      {u}
                    </button>
                  ))}
                </div>
                <p style={{ marginTop: 6 }}>backend/config/seed.dev.json</p>
              </details>
            )}
          </div>
          <AppDownloadCard />
          <div className="login-stats-mobile">
            <LoginStats compact />
          </div>
          <p className="small muted" style={{ textAlign: 'center' }}>
            © {new Date().getFullYear()} {t('app.fullName')}
          </p>
        </div>
      </section>
    </div>
  );
}

/** Decorative, animated hero background: drifting light, an ECG trace and rising crosses. */
function LoginBackdrop() {
  return (
    <div className="login-backdrop" aria-hidden>
      <span className="orb o1" />
      <span className="orb o2" />
      <span className="orb o3" />
      <svg className="login-ecg" viewBox="0 0 600 120" preserveAspectRatio="none">
        <path
          className="trace"
          pathLength={1}
          d="M0 60 H140 L155 60 L165 35 L178 92 L192 12 L206 104 L218 60 H300 L312 60 L320 48 L330 60 H420 L435 60 L445 35 L458 92 L472 12 L486 104 L498 60 H600"
        />
        <path
          className="pulse"
          pathLength={1}
          d="M0 60 H140 L155 60 L165 35 L178 92 L192 12 L206 104 L218 60 H300 L312 60 L320 48 L330 60 H420 L435 60 L445 35 L458 92 L472 12 L486 104 L498 60 H600"
        />
      </svg>
      {[12, 28, 46, 63, 79, 90].map((left, i) => (
        <span key={left} className="cross" style={{ left: `${left}%`, ['--d' as string]: `${i * 1.7}s`, ['--s' as string]: `${0.6 + (i % 3) * 0.25}` }}>
          +
        </span>
      ))}
    </div>
  );
}
